export const statuses = [
  "Prepedido",
  "Pendiente",
  "Aceptado",
  "Pagado",
  "En ruta",
  "Entregado",
  "Cancelado",
];
export const paidStatuses = ["Pagado", "En ruta", "Entregado"];
export const money = (value) =>
  new Intl.NumberFormat("es-PE", { style: "currency", currency: "PEN" }).format(
    Number(value) || 0,
  );
export const uid = () => crypto.randomUUID();
export const round = (n) =>
  Math.round((Number(n) + Number.EPSILON) * 100) / 100;
export const date = (value) =>
  new Date(value).toLocaleString("es-PE", {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: "America/Lima",
  });
export function priceFor(product, quantity = 1) {
  return product.wholesale_price > 0 &&
    product.wholesale_min > 0 &&
    quantity >= product.wholesale_min
    ? Math.min(product.price, product.wholesale_price)
    : product.price;
}
export function buildOrder(data, payload, channel = "WEB") {
  if (
    !payload.customer_name?.trim() ||
    !/^[+\d\s()-]{7,20}$/.test(payload.phone || "")
  )
    throw new Error("Completa el nombre y un celular válido.");
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(payload.email || ""))
    throw new Error("Escribe un correo válido.");
  if (!["Yape", "Plin", "Transferencia"].includes(payload.payment))
    throw new Error("Elige un método de pago.");
  if (!["delivery", "pickup"].includes(payload.delivery))
    throw new Error("Elige un método de entrega.");
  if (payload.delivery === "delivery" && !payload.address?.trim())
    throw new Error("Escribe la dirección de entrega.");
  if (!payload.items?.length || payload.items.length > 100)
    throw new Error("Agrega al menos un producto.");
  const quantities = new Map();
  for (const item of payload.items) {
    if (
      !Number.isInteger(item.quantity) ||
      item.quantity < 1 ||
      item.quantity > 999
    )
      throw new Error("Cantidad inválida.");
    quantities.set(
      item.variant_id,
      (quantities.get(item.variant_id) || 0) + item.quantity,
    );
  }
  const items = [...quantities].map(([id, quantity]) => {
    const v = data.variants.find((v) => v.id === id && v.active);
    const p = data.products.find((p) => p.id === v?.product_id && p.active);
    if (!p || !v) throw new Error("Un producto ya no está disponible.");
    if (quantity > v.stock)
      throw new Error(
        `Stock insuficiente: ${p.name}, ${v.color}. Disponibles: ${v.stock}.`,
      );
    const productQuantity = [...quantities].reduce(
      (sum, [vid, q]) =>
        sum +
        (data.variants.find((v) => v.id === vid)?.product_id === p.id ? q : 0),
      0,
    );
    const unit_price = priceFor(p, productQuantity);
    return {
      id: uid(),
      product_id: p.id,
      variant_id: v.id,
      name: p.name,
      sku: v.sku,
      color: v.color,
      category_id: p.category_id,
      quantity,
      unit_price,
      total: round(unit_price * quantity),
    };
  });
  const subtotal = round(items.reduce((s, x) => s + x.total, 0));
  const shipping =
    payload.delivery === "pickup" ||
    (data.store.free_shipping > 0 && subtotal >= data.store.free_shipping)
      ? 0
      : Number(data.store.shipping_fee || 0);
  return {
    id: uid(),
    number: `DILO-${Date.now().toString(36).toUpperCase()}`,
    created_at: new Date().toISOString(),
    status:
      channel === "WEB"
        ? "Pendiente"
        : payload.preorder
          ? "Prepedido"
          : "Pendiente",
    channel,
    customer_name: payload.customer_name.trim(),
    phone: payload.phone.trim(),
    email: payload.email.trim(),
    address: payload.address || "",
    payment: payload.payment,
    delivery: payload.delivery,
    notes: payload.notes || "",
    collaborator_id: payload.collaborator_id || null,
    request_key: payload.request_key,
    subtotal,
    shipping,
    total: round(subtotal + shipping),
    items,
    paid_at: null,
    review_token: uid(),
    review: null,
  };
}
export function nextStatusAllowed(from, to) {
  if (from === to) return true;
  if (from === "Cancelado") return false;
  if (to === "Cancelado") return true;
  return statuses.indexOf(to) > statuses.indexOf(from);
}
export function csvEncode(rows) {
  return (
    "\uFEFF" +
    rows
      .map((row) =>
        row
          .map((value) => {
            let s = String(value ?? "");
            if (/^[=+@\-\t\r]/.test(s)) s = "'" + s;
            return '"' + s.replaceAll('"', '""') + '"';
          })
          .join(","),
      )
      .join("\r\n")
  );
}
export function csvParse(text) {
  const rows = [];
  let row = [],
    cell = "",
    quoted = false;
  text = text.replace(/^\uFEFF/, "");
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (c === '"' && quoted && text[i + 1] === '"') {
      cell += '"';
      i++;
    } else if (c === '"') quoted = !quoted;
    else if (c === "," && !quoted) {
      row.push(cell);
      cell = "";
    } else if (c === "\n" && !quoted) {
      row.push(cell.replace(/\r$/, ""));
      rows.push(row);
      row = [];
      cell = "";
    } else cell += c;
  }
  if (quoted) throw new Error("CSV con comillas sin cerrar.");
  if (cell || row.length) {
    row.push(cell.replace(/\r$/, ""));
    rows.push(row);
  }
  return rows.filter((r) => r.some(Boolean));
}
export const safeUrl = (value) => {
  if (!value) return "";
  if (/^\/assets\/[a-z0-9._-]+$/i.test(value)) return value;
  if (
    /^data:(image\/(png|jpeg|webp|gif)|video\/(mp4|webm));base64,/i.test(value)
  )
    return value;
  try {
    const u = new URL(value);
    return u.protocol === "https:" ? u.href : "";
  } catch {
    return "";
  }
};
